import React from "react";
import { UseCase, ReviewStatus } from "../../types/TestTarget";
import { FormGroup, Label, TextArea, ViewValue, Input, Button, StatusBadge } from "./styles";
import { AutoAwesome as AutoAwesomeIcon } from "@mui/icons-material";
import { CircularProgress, Tooltip } from "@mui/material";
import { STATUS_LABELS } from "../../utils/statusUtils";

interface BasicInfoTabProps {
  editedUseCase: UseCase;
  setEditedUseCase: React.Dispatch<React.SetStateAction<UseCase | null>>;
  mode: "view" | "edit" | "create";
  onGenerateSteps?: () => void;
  isGenerating?: boolean;
  /** #884: ユーザー可視の呼称（機能タブからは「機能」）。既定は「ユースケース」。 */
  entityLabel?: string;
}

export const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  editedUseCase,
  setEditedUseCase,
  mode,
  onGenerateSteps,
  isGenerating = false,
  entityLabel = "ユースケース",
}) => {
  return (
    <>
      {mode === "view" ? (
        <>
          <FormGroup>
            <Label htmlFor="usecase_id">{entityLabel}ID</Label>
            <ViewValue style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
              {editedUseCase.usecase_id || "-"}
            </ViewValue>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="title">タイトル</Label>
            <ViewValue>{editedUseCase.title || "タイトルなし"}</ViewValue>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="description">説明</Label>
            <ViewValue style={{ minHeight: "120px" }}>
              {editedUseCase.description || "説明なし"}
            </ViewValue>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="status">ステータス</Label>
            <ViewValue>
              <StatusBadge status={editedUseCase.status || "approved"}>
                {STATUS_LABELS[editedUseCase.status as ReviewStatus] || STATUS_LABELS.approved}
              </StatusBadge>
            </ViewValue>
          </FormGroup>
        </>
      ) : (
        <>
          {editedUseCase.usecase_id && (
            <FormGroup>
              <Label htmlFor="usecase_id">{entityLabel}ID</Label>
              <ViewValue style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                {editedUseCase.usecase_id}
              </ViewValue>
            </FormGroup>
          )}

          <FormGroup>
            <Label htmlFor="title">タイトル</Label>
            <Input
              id="title"
              value={editedUseCase.title || ""}
              onChange={(e) =>
                setEditedUseCase((prev) =>
                  prev ? { ...prev, title: e.target.value, name: e.target.value } : null
                )
              }
              placeholder={`${entityLabel}のタイトルを入力してください`}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="description">説明</Label>
            <TextArea
              id="description"
              value={editedUseCase.description}
              onChange={(e) =>
                setEditedUseCase((prev) =>
                  prev ? { ...prev, description: e.target.value } : null
                )
              }
              placeholder={`${entityLabel}の説明を入力してください`}
              style={{ minHeight: "150px" }}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="status">ステータス</Label>
            <select
              id="status"
              value={editedUseCase.status || "approved"}
              onChange={(e) =>
                setEditedUseCase((prev) =>
                  prev ? { ...prev, status: e.target.value as ReviewStatus } : null
                )
              }
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "0.875rem",
              }}
            >
              {(Object.keys(STATUS_LABELS) as ReviewStatus[]).map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </FormGroup>

          {onGenerateSteps && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
              <Tooltip
                title={
                  !editedUseCase.title
                    ? "タイトルを入力してください"
                    : !editedUseCase.description
                    ? "説明を入力してください"
                    : ""
                }
                arrow
                placement="top"
              >
                <span>
                  <Button
                    variant="primary"
                    onClick={onGenerateSteps}
                    disabled={isGenerating || !editedUseCase.title || !editedUseCase.description}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      opacity: isGenerating || !editedUseCase.title || !editedUseCase.description ? 0.5 : 1,
                    }}
                  >
                    {isGenerating ? (
                      <>
                        <CircularProgress size={16} style={{ color: "white" }} />
                        ステップ生成中...
                      </>
                    ) : (
                      <>
                        <AutoAwesomeIcon style={{ fontSize: "1rem" }} />
                        AIでステップを生成
                      </>
                    )}
                  </Button>
                </span>
              </Tooltip>
            </div>
          )}
        </>
      )}
    </>
  );
};